import React, { useState, useEffect } from 'react';
import { formatDate } from '../utils/dateUtils';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { TGChibiVariants } from '../components/TGChibi';
import SEO from '../components/SEO';
import {
  GlobeAltIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ... existing code ...
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, scansResponse] = await Promise.all([
        api.get('/scans/summary'),
        api.get('/scans/?limit=5')
      ]);

      setStats(statsResponse.data);
      setRecentScans(scansResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Ensure recentScans is always an array even on error
      setRecentScans([]);
    } finally {
      setLoading(false);
    }
  };



  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'running': return 'text-mint-600';
      case 'failed': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <TGChibiVariants.Loading message="Loading your dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      <SEO
        title="Dashboard"
        description="Automated DNS scanner with Wildcard Shield. Identify high-risk domains (Score >= 8) using age, SSL, MX records, and content similarity."
        ignoreCanonical={true}
      />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Monitor your domains and track typosquatting threats
          </p>

        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white dark:bg-dark-800 overflow-hidden shadow-glow rounded-lg border border-gray-200 dark:border-dark-600">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <GlobeAltIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Domains
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {stats?.domains_monitored || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 overflow-hidden shadow-glow rounded-lg border border-gray-200 dark:border-dark-600">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentMagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Scans
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {stats?.total_scans || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 overflow-hidden shadow-glow rounded-lg border border-gray-200 dark:border-dark-600">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      High Risk Findings
                    </dt>
                    <dd className="text-lg font-medium text-red-600 dark:text-red-400">
                      {stats?.recent_high_risk || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 overflow-hidden shadow-glow rounded-lg border border-gray-200 dark:border-dark-600">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Whitelisted Findings
                    </dt>
                    <dd className="text-lg font-medium text-green-600 dark:text-green-400">
                      {stats?.recent_whitelisted || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-dark-800 shadow-glow rounded-lg mb-8 border border-gray-200 dark:border-dark-600">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to="/domains"
                className="relative group bg-gray-50 dark:bg-dark-700 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-mint-500 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-mint-50 dark:bg-mint-900/20 text-mint-600 dark:text-mint-400">
                    <GlobeAltIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Add Domain
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Add a new domain to monitor for typosquatting
                  </p>
                </div>
              </Link>

              <Link
                to="/scans"
                className="relative group bg-gray-50 dark:bg-dark-700 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-mint-500 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-mint-50 dark:bg-mint-900/20 text-mint-600 dark:text-mint-400">
                    <DocumentMagnifyingGlassIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Run Scan
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Start a new typosquatting scan
                  </p>
                </div>
              </Link>

              <Link
                to="/reports"
                className="relative group bg-gray-50 dark:bg-dark-700 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-mint-500 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-mint-50 dark:bg-mint-900/20 text-mint-600 dark:text-mint-400">
                    <DocumentMagnifyingGlassIcon className="h-6 w-6" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    View Reports
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Access detailed scan reports
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Scans */}
        <div className="bg-white dark:bg-dark-800 rounded-xl shadow-glow overflow-hidden border border-gray-200 dark:border-dark-600">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                Recent Scans
              </h3>
              <Link
                to="/scans"
                className="text-sm text-mint-600 hover:text-mint-500 dark:text-mint-400 dark:hover:text-mint-300"
              >
                View all
              </Link>
            </div>

            {(!recentScans || recentScans.length === 0) ? (
              <div className="text-center py-8">
                <TGChibiVariants.Empty />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No scans yet</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Get started by adding a domain and running your first scan.
                </p>
                <div className="mt-6">
                  <Link
                    to="/domains"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                  >
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                    Add Domain
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flow-root">
                <ul className="-my-5 divide-y divide-gray-200 dark:divide-dark-600">
                  {(recentScans || []).map((scan) => (
                    <li key={scan.scan_id} className="py-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <DocumentMagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {scan.domain?.domain_name || 'Unknown Domain'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(scan.started_at)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(scan.status)}`}>
                            {scan.status}
                          </span>
                          {scan.findings_count > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300">
                              {scan.findings_count} findings
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
